import { useState, useRef, useLayoutEffect, useEffect } from "react";
import ReactDOM from "react-dom";
import { CountBadge } from "./ui";
import { wrappableName } from "../utils";
import { useEscapeKey } from "../hooks/useEscapeKey";
import { useAppTheme } from "../contexts/ThemeContext";

/**
 * ProfileMenu — the avatar/name chip in the top-right of the header plus its
 * dropdown menu, shared by the in-gym header (App.js) and the gym-selector
 * header (GymSelector.js) so both render the exact same control.
 *
 * Design: "stepped merged frame". Closed, it's the usual narrow pill. Open,
 * the SAME name-frame stays exactly the same width/height (measured from the
 * closed chip, so it never grows and never crowds the top row) while a single
 * continuous 1px border flows down out of it — via a concave (negative-radius)
 * step on the left — into a wider menu list. The whole outline is one SVG path
 * so the step and the stroke stay crisp; the real chip + list are layered on
 * top. The outline (closed pill border + open frame stroke) is accent-colored
 * in both states; the interior stays plain `th.card` (no gradient).
 *
 * Props:
 *  - theme, accent, font          — th, gym/platform accent, font family (F)
 *  - avatarUrl                    — photo URL or null (falls back to initials)
 *  - initials                     — initials string for the fallback avatar
 *  - name                         — display name
 *  - showPaBadge                  — small "PA" pill on the chip (platform admin)
 *  - notifCount                   — badge on the chip + on the item with badge:true
 *  - items                        — [{ key, icon, label, onClick?, href?, target?,
 *                                      rel?, badge?, muted? }]
 */

const RL = 18;        // list corner radius
const RS = 18;        // shelf top-left corner radius
const RC = 14;        // concave step radius (the "negative" corner)
const ITEM_H = 44;    // fixed height per menu row → deterministic list height
const PAD = 0.75;     // inset so the 1px stroke stays inside the viewBox
const MIN_STEP = 36;  // if the list must be wider than the name, at least this
                      // much — a smaller concave step can't fit RS + RC cleanly
const NAME_W = 150;   // widest the name may get on one line
const NAME_LINES = 3; // how far it may wrap before the ellipsis gives up

// Full outline as one SVG path. `px` = how much wider the list is than the name
// (the left overhang). px === 0 → the list is exactly the name width, so the
// frame is a plain rounded rect (no step) — this is the long-name case. px > 0
// → the name pill stays right-aligned and the border flows down-left via a
// concave step into the wider list (the short-name case, where a menu row is
// wider than the name).
function buildFramePath(w, hp, listH, px) {
  const rp = hp / 2;                         // top corners match the pill's ends
  const W = w + px, H = hp + listH, p = PAD;
  const xr = W - p, xl = p, yt = p, yb = H - p;
  const A = (r, sw, x, y) => `A${r} ${r} 0 0 ${sw} ${x} ${y}`;
  if (px <= 0) {
    // straight rounded rect: pill-round top (rp), list-round bottom (RL)
    return [
      `M${xl + rp} ${yt}`,
      `L${xr - rp} ${yt}`, A(rp, 1, xr, yt + rp),      // top-right
      `L${xr} ${yb - RL}`, A(RL, 1, xr - RL, yb),      // right edge → bottom-right
      `L${xl + RL} ${yb}`, A(RL, 1, xl, yb - RL),      // bottom → bottom-left
      `L${xl} ${yt + rp}`, A(rp, 1, xl + rp, yt),      // left edge → top-left
      "Z",
    ].join(" ");
  }
  return [
    `M${px + rp} ${yt}`,
    `L${xr - rp} ${yt}`, A(rp, 1, xr, yt + rp),        // pill top-right
    `L${xr} ${yb - RL}`, A(RL, 1, xr - RL, yb),        // shared right edge → bottom-right
    `L${xl + RL} ${yb}`, A(RL, 1, xl, yb - RL),        // bottom → bottom-left
    `L${xl} ${hp + RS}`, A(RS, 1, xl + RS, hp),        // list left edge → shelf top-left
    `L${px - RC} ${hp}`, A(RC, 0, px, hp - RC),        // shelf → concave step (sweep 0)
    `L${px} ${yt + rp}`, A(rp, 1, px + rp, yt),        // pill left edge → pill top-left
    "Z",
  ].join(" ");
}

// Below the pinned header's own stacking context (z-index 50 in App.js and
// GlobalProfile.js) and above page content (1–5). See the backdrop below.
const Z_MENU_BACKDROP = 40;

export default function ProfileMenu({
  font: F,
  avatarUrl,
  initials,
  name,
  showPaBadge = false,
  notifCount = 0,
  items = [],
  onOpenChange,
}) {
  const { th, accent } = useAppTheme();
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(null);
  const [dims, setDims] = useState({ w: 0, hp: 0 });
  const [contentW, setContentW] = useState(0);
  const chipRef = useRef(null);
  const probeRef = useRef(null);

  // Measure the closed chip so the open head is byte-identical in size. A
  // ResizeObserver keeps it correct across async name loads, font swaps, zoom.
  useLayoutEffect(() => {
    const el = chipRef.current;
    if (!el) return;
    const measure = () => setDims({ w: el.offsetWidth, hp: el.offsetHeight });
    measure();
    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(measure);
      ro.observe(el);
    }
    return () => ro && ro.disconnect();
  }, [name, showPaBadge]);

  // Measure the widest menu row from a hidden probe, so the list only gets a
  // left overhang when a row is genuinely wider than the name (long names →
  // no overhang). Re-measures on label change and after the webfont loads.
  const labelsKey = items.map((it) => (it.badge ? "#" : "") + it.icon + it.label).join("|");
  useLayoutEffect(() => {
    const measure = () => {
      const el = probeRef.current;
      if (!el) return;
      let max = 0;
      for (const child of el.children) max = Math.max(max, child.offsetWidth);
      setContentW(Math.ceil(max) + 1);
    };
    measure();
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(measure);
  }, [labelsKey]);

  // Close on Escape while open; clear any stale hover when the menu closes.
  // The panel hangs off the chip, so whoever owns the chrome around it needs to
  // know: the gym shell freezes its collapsing header while this is open,
  // otherwise the header slides away and leaves the menu attached to nothing.
  useEffect(() => { onOpenChange?.(open); }, [open, onOpenChange]);

  useEffect(() => { if (!open) setHovered(null); }, [open]);
  useEscapeKey(() => setOpen(false), open);

  const rp = dims.hp / 2;
  const listH = items.length * ITEM_H;
  // Left overhang: only when the widest menu row is wider than the name, and
  // then at least MIN_STEP so the concave step fits. Never narrower than the
  // name (px >= 0 → list width = name width + px >= name width).
  const overhang = contentW - dims.w;
  const px = overhang > 0 ? Math.max(overhang, MIN_STEP) : 0;
  const W = dims.w + px;
  const H = dims.hp + listH;
  const canDrawFrame = open && dims.w > 0 && dims.hp > 0;
  const d = canDrawFrame ? buildFramePath(dims.w, dims.hp, listH, px) : "";
  const accentTint = accent + "17"; // ~9% — hover tint

  const runItem = (e, item) => {
    setOpen(false);
    setHovered(null);
    if (item.onClick) item.onClick(e);
  };

  // Chip (trigger + open head) — same box in both states thanks to border-box
  // + a constant 1px border whose colour just turns transparent when open.
  const chipStyle = {
    position: "relative",
    zIndex: open ? 62 : undefined,
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "4px 12px 4px 6px",
    boxSizing: "border-box",
    cursor: "pointer",
    fontFamily: F,
    backgroundColor: open ? "transparent" : th.card,
    // constant 1px accent border; open turns all sides transparent (the SVG
    // draws the accent outline). The name↔menu divider lives on the first list
    // row (at the exact seam) instead of the chip bottom, so the avatar sits
    // centered between the top accent border and that divider. Box unchanged.
    border: "1px solid " + (open ? "transparent" : accent),
    borderRadius: open ? `${rp}px ${rp}px 0 0` : 999,
  };

  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      {/* hidden probe: measures each menu row's natural width so the list only
          overhangs the name when a row is actually wider than it */}
      <div
        ref={probeRef}
        aria-hidden="true"
        style={{ position: "absolute", top: 0, left: 0, height: 0, overflow: "hidden", visibility: "hidden", pointerEvents: "none" }}
      >
        {items.map((item) => (
          <span
            key={item.key}
            style={{ position: "absolute", display: "inline-flex", alignItems: "center", gap: 10, padding: "0 16px", fontSize: 13, fontWeight: 600, fontFamily: F, whiteSpace: "nowrap" }}
          >
            <span style={{ fontSize: 15, width: 18, textAlign: "center", flexShrink: 0 }}>{item.icon}</span>
            <span>{item.label}</span>
            {item.badge && <span style={{ width: 20, marginLeft: 8, flexShrink: 0 }} />}
          </span>
        ))}
      </div>

      {/* Click-outside backdrop, PORTALLED to <body>.

          It is `position: fixed; inset: 0`, and fixed means "the viewport"
          only while no ancestor has a transform — a transformed ancestor
          becomes the containing block instead. This menu sits inside the
          pinned header block, and that block is translated as it collapses on
          scroll (useHeaderCollapse), so rendered in place the backdrop covered
          just the header strip: the menu opened fine and then would not close
          on a click anywhere in the page below it. In a portal it has no
          ancestor to be trapped by. */}
      {open && ReactDOM.createPortal(
        <div
          onClick={() => setOpen(false)}
          // UNDER the pinned header, not over it. The header is a positioned
          // element with `z-index: 50`, so it is a stacking context: everything
          // inside it — this menu included — is stacked as a unit at 50 against
          // anything outside, and the panel's own z-indexes only order it
          // WITHIN the header. A backdrop portalled to <body> at 60 therefore
          // covered the menu, and every item in it swallowed the tap instead of
          // firing. Below the header it covers the whole page except the header
          // itself, which is exactly what "click outside to close" means here.
          style={{ position: "fixed", inset: 0, zIndex: Z_MENU_BACKDROP }}
        />,
        document.body,
      )}

      {/* single continuous outline behind everything */}
      {canDrawFrame && (
        <svg
          width={W}
          height={H}
          viewBox={`0 0 ${W} ${H}`}
          shapeRendering="geometricPrecision"
          aria-hidden="true"
          style={{
            position: "absolute",
            top: 0,
            right: 0,
            overflow: "visible",
            zIndex: 61,
            pointerEvents: "none",
            filter: "drop-shadow(0 6px 16px rgba(0,0,0,0.18))",
          }}
        >
          <path d={d} fill={th.card} stroke={accent} strokeWidth="1" />
        </svg>
      )}

      {/* the chip: trigger when closed, the menu's crown when open */}
      <button
        ref={chipRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={name}
        style={chipStyle}
      >
        {/* notification chip (friend requests + push-notif discovery signal) */}
        <CountBadge
          count={notifCount}
          style={{ position: "absolute", top: -4, right: -4, border: "2px solid " + th.bg }}
        />
        {avatarUrl ? (
          <img
            src={avatarUrl}
            alt=""
            style={{ width: 26, height: 26, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }}
          />
        ) : (
          <div
            style={{
              width: 26, height: 26, borderRadius: "50%", background: accent, color: "#fff",
              display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12,
              fontWeight: 700, flexShrink: 0,
            }}
          >
            {initials}
          </div>
        )}
        {/* One line while the row can spare the width; it only wraps once the
            gym name and the points beside it start running out of room. A
            person's name is not something to cut off with an ellipsis, so the
            chip yields by wrapping rather than truncating.

            `min-width: min-content` is what makes that work: it is the floor
            the flex row may squeeze the name to — the longest single word — so
            the text always has room to lay itself out, instead of being
            clipped the way `min-width: 0` allows. `wrappableName` swaps the
            hyphen for U+2011 so "Elszánt Béta-mester" breaks after "Elszánt"
            and not after the hyphen (`word-break: keep-all` does NOT suppress
            that break in Chrome). `break-word` is the last resort for a single
            word wider than the whole box: mid-word is ugly, but it still shows
            the name, and without it a `-webkit-box` clips it mid-glyph with no
            ellipsis at all. Only a name too long for even NAME_LINES lines —
            three words on a ~360px screen — gets the clamp's ellipsis. */}
        <span
          style={{
            fontSize: 13, fontWeight: 600, color: th.text,
            maxWidth: NAME_W, minWidth: "min-content",
            lineHeight: 1.25, display: "-webkit-box", WebkitLineClamp: NAME_LINES,
            WebkitBoxOrient: "vertical", overflow: "hidden", overflowWrap: "break-word",
          }}
        >
          {wrappableName(name)}
        </span>
        {showPaBadge && (
          <span
            style={{
              fontSize: 12, fontWeight: 700, color: "#d97706", background: "#d9770622",
              padding: "1px 5px", borderRadius: 4,
            }}
          >
            PA
          </span>
        )}
      </button>

      {/* the list — layered on top of the SVG, clipped to the rounded corners */}
      {open && (
        <div
          role="menu"
          style={{
            position: "absolute",
            top: dims.hp,
            right: 0,
            width: W,
            zIndex: 62,
            // stepped: round the shelf top-left (RS). straight (no step): the
            // top continues the pill's straight sides, so only the bottom rounds.
            borderRadius: px > 0 ? `${RS}px 0 ${RL}px ${RL}px` : `0 0 ${RL}px ${RL}px`,
            overflow: "hidden",
          }}
        >
          {items.map((item, i) => {
            // Row 0's top line is the name↔menu divider: it spans only under the
            // name (right-aligned, inset 16 each side) so it doesn't run left
            // across the step/overhang and over the accent border. The other
            // rows' dividers span the full list width, inset 16 each side.
            const nameDivider = i === 0;
            const rowStyle = {
              display: "flex",
              alignItems: "center",
              gap: 10,
              width: "100%",
              height: ITEM_H,
              padding: "0 16px",
              boxSizing: "border-box",
              backgroundColor: hovered === item.key ? accentTint : "transparent",
              // divider inset from the accent frame (background line, not a
              // full-width border that would touch the outline)
              backgroundImage: `linear-gradient(${th.border}, ${th.border})`,
              backgroundRepeat: "no-repeat",
              backgroundSize: nameDivider ? `${Math.max(dims.w - 32, 0)}px 1px` : "calc(100% - 32px) 1px",
              backgroundPosition: nameDivider ? "right 16px top" : "center top",
              border: "none",
              color: item.muted ? th.textMuted : th.text,
              fontSize: 13,
              fontWeight: 600,
              fontFamily: F,
              textAlign: "left",
              textDecoration: "none",
              cursor: "pointer",
            };
            const inner = (
              <>
                <span style={{ fontSize: 15, width: 18, textAlign: "center", flexShrink: 0 }} aria-hidden="true">
                  {item.icon}
                </span>
                <span style={{ whiteSpace: "nowrap" }}>{item.label}</span>
                {item.badge && <CountBadge count={notifCount} style={{ marginLeft: "auto" }} />}
              </>
            );
            const shared = {
              role: "menuitem",
              style: rowStyle,
              onMouseEnter: () => setHovered(item.key),
              onMouseLeave: () => setHovered((h) => (h === item.key ? null : h)),
              onClick: (e) => runItem(e, item),
            };
            return item.href ? (
              <a key={item.key} {...shared} href={item.href} target={item.target} rel={item.rel}>
                {inner}
              </a>
            ) : (
              <button key={item.key} {...shared} type="button">
                {inner}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
